import { Tier } from "../../../api/tiersApi";
import { SubscriptionStatus } from "../../../types/subscription";
import { Subscription } from "../types/Subscription";

export interface TierChangeInfo {
  willChangeTier: boolean;
  currentTier: string;
  newTier: string;
  currentUserCount: number;
  newUserCount: number;
  requiresManualUpgrade: boolean;
  currentTotalCost: number;
  newTotalCost: number;
  currentPricePerSeat: number;
  newPricePerSeat: number;
  currentCurrency: string;
  newCurrency: string;
  proratedAmountCents: number;
  /** Processing fee in cents when passed to customer (from seat change impact API) */
  processingFeeCents?: number;
}

export const TIER_THRESHOLDS = {
  ESSENTIALS_MAX: 5,
  PREMIUM_MAX: 10,
};

export const getTierForUserCount = (userCount: number): string => {
  if (userCount <= TIER_THRESHOLDS.ESSENTIALS_MAX) {
    return "essentials";
  }
  if (userCount <= TIER_THRESHOLDS.PREMIUM_MAX) {
    return "premium";
  }
  return "enterprise";
};

export const normalizeTierName = (name: string): string => {
  const lowerName = (name || "").toLowerCase();
  if (lowerName === "basic" || lowerName === "trial") {
    return "essentials";
  }
  return lowerName;
};

export const getTierSeatLimit = (tierName: string): number | null => {
  const normalizedName = normalizeTierName(tierName);
  if (normalizedName === "essentials") {
    return TIER_THRESHOLDS.ESSENTIALS_MAX;
  }
  if (normalizedName === "premium") {
    return TIER_THRESHOLDS.PREMIUM_MAX;
  }
  return null; // Enterprise or unknown
};

export const findTierByName = (
  tiers: Tier[],
  name: string,
): Tier | undefined => {
  const normalizedName = normalizeTierName(name);
  return tiers.find((tier) => normalizeTierName(tier.name) === normalizedName);
};

export const getTierPricing = (
  tier: Tier | undefined,
  region: string = "US",
) => {
  if (!tier) {
    return { pricePerSeat: 0, currency: "USD" };
  }

  const regionalOverride = tier.regionalOverrides?.find(
    (override) => override.region === region,
  );

  return {
    pricePerSeat: regionalOverride?.price || tier.pricePerSeat,
    currency: regionalOverride?.currency || "USD",
  };
};

export const calculateTierChange = (
  subscription: Subscription,
  allTiers: Tier[],
  deltaSeats: number,
): TierChangeInfo => {
  const currentPhysicianCount = subscription.quantity || 0;
  const newPhysicianCount = Math.max(0, currentPhysicianCount + deltaSeats);

  const currentTierName = normalizeTierName(subscription.tier);
  const newTierName = getTierForUserCount(newPhysicianCount);

  const currentTier = findTierByName(allTiers, currentTierName);
  const newTier = findTierByName(allTiers, newTierName);

  const currentPricing = getTierPricing(
    currentTier,
    subscription.tierInfo?.price.currency || "USD",
  );
  const newPricing = getTierPricing(
    newTier,
    subscription.tierInfo?.price.currency || "USD",
  );

  const currentTotalCost = currentPhysicianCount * currentPricing.pricePerSeat;
  let newTotalCost = newPhysicianCount * newPricing.pricePerSeat;

  // Special handling for enterprise: custom pricing (pricePerSeat=0)
  if (newTierName === "enterprise" && newPricing.pricePerSeat === 0) {
    newTotalCost = currentTotalCost; // Assume no change until manual upgrade is done
  }

  return {
    willChangeTier: currentTierName !== newTierName,
    currentTier: currentTierName,
    newTier: newTierName,
    currentUserCount: currentPhysicianCount,
    newUserCount: newPhysicianCount,
    requiresManualUpgrade:
      newTierName === "enterprise" && newPricing.pricePerSeat === 0,
    currentTotalCost,
    newTotalCost,
    currentPricePerSeat: currentPricing.pricePerSeat,
    newPricePerSeat: newPricing.pricePerSeat,
    currentCurrency: currentPricing.currency,
    newCurrency: newPricing.currency,
    proratedAmountCents: 0,
  };
};

export const formatPrice = (
  amountCents: number,
  currency: string = "USD",
): string => {
  const amountDollars = amountCents / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(amountDollars);
};

export const getTierDisplayName = (tierName: string): string => {
  const normalizedName = normalizeTierName(tierName);
  if (normalizedName === "essentials") {
    return "Essentials";
  }
  if (normalizedName === "premium") {
    return "Premium";
  }
  if (normalizedName === "enterprise") {
    return "Enterprise";
  }
  return "Unknown Tier";
};

export const wouldExceedTierLimit = (
  subscription: Subscription,
  deltaSeats: number,
): boolean => {
  const currentPhysicianCount = subscription.quantity || 0;
  const newPhysicianCount = currentPhysicianCount + deltaSeats;
  const currentTierName = normalizeTierName(subscription.tier);
  const currentTierLimit = getTierSeatLimit(currentTierName);

  if (currentTierLimit === null) {
    // Enterprise or unlimited
    return false;
  }

  return newPhysicianCount > currentTierLimit;
};

export const couldDowngradeTier = (
  subscription: Subscription,
  deltaSeats: number,
): boolean => {
  const currentPhysicianCount = subscription.quantity || 0;
  const newPhysicianCount = Math.max(0, currentPhysicianCount + deltaSeats);

  const currentTierName = normalizeTierName(subscription.tier);
  const newTierName = getTierForUserCount(newPhysicianCount);

  return (
    currentTierName !== newTierName && newPhysicianCount < currentPhysicianCount
  );
};

export const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  [SubscriptionStatus.ACTIVE]: "Active",
  [SubscriptionStatus.INACTIVE]: "Inactive",
  [SubscriptionStatus.PAST_DUE]: "Past Due",
  [SubscriptionStatus.UNPAID]: "Unpaid",
  [SubscriptionStatus.CANCELED]: "Cancelled",
  [SubscriptionStatus.CANCELLATION_REQUESTED]: "Cancellation Requested",
  [SubscriptionStatus.CANCELING]: "Canceling",
  [SubscriptionStatus.TRIALING]: "Trialing",
  [SubscriptionStatus.PAUSED]: "Paused",
  [SubscriptionStatus.INCOMPLETE]: "Incomplete",
  [SubscriptionStatus.INCOMPLETE_EXPIRED]: "Incomplete Expired",
  [SubscriptionStatus.COMPLETE]: "Complete",
};

export const formatSubscriptionStatus = (
  status: string | undefined,
): string => {
  if (!status) return "Unknown";
  return (
    SUBSCRIPTION_STATUS_LABELS[status] ||
    status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
  );
};

export const formatSubscriptionInterval = (
  interval: string | undefined,
): string => {
  if (!interval) return "";
  const lower = interval.toLowerCase();
  if (lower === "month") return "Monthly";
  if (lower === "quarter") return "Quarterly";
  if (lower === "year") return "Yearly";
  return interval.charAt(0).toUpperCase() + interval.slice(1);
};
